/**
 * Zod schemas + DTO types for the Users API surface — shared between
 * API (validation) and Web (form state + types).
 *
 * Phase 2 (Wave 1, V2-FIX-04): `fullName` is now part of the user
 * summary (research-B G1, migration 007). All schemas are `.strict()` so
 * a smuggled extra key is rejected as VALIDATION_ERROR.
 */
import { z } from 'zod';
import { EmailSchema, FullNameSchema, PasswordSchema, PhoneSchema } from './auth';
import { UserStatus, RoleSlug, AccessLevel } from './enums';
import { ruEnum } from './lib/zod-ru';

/** Cursor-pagination cursor: opaque base64(JSON({ createdAt: ISO, id })). */
export const UserListCursorSchema = z.string().min(1).max(512);

/**
 * `GET /users` query schema. Phase 2 keeps the cursor escape-hatch alive
 * for backward compatibility (FE will swap to offset pagination in W3),
 * but the canonical paginator is `(page, limit)`.
 */
export const ListUsersQuerySchema = z
  .object({
    status: z.nativeEnum(UserStatus, ruEnum('Недопустимый статус')).optional(),
    departmentId: z.coerce.number().int().positive().optional(),
    roleSlug: z.nativeEnum(RoleSlug, ruEnum('Недопустимая роль')).optional(),
    /**
     * Filter by access level (joins through Role.accessLevel). Optional;
     * single value. Used by the supervisor "Emergency Admin recovery"
     * UI to surface only ADMIN-tier active users.
     */
    accessLevel: z.nativeEnum(AccessLevel, ruEnum('Недопустимый уровень доступа')).optional(),
    search: z.string().trim().min(1).max(200).optional(),
    /**
     * Блок 2 · Ф8 · В-З1 — признак запроса: список людей нужен «Загруженности»
     * (решение плана фазы П8).
     *
     * Только с ним руководитель отдела, которому добавлено право «вся
     * загруженность бюро», получает людей ВСЕХ отделов. Без признака сужение
     * по отделу остаётся — иначе право на загруженность открыло бы ему весь
     * справочник людей на каждом экране.
     *
     * ⚠️ Признак сам ничего не открывает: без права он инертен, и запрос
     * чужого отдела по-прежнему получает отказ. Значение одно; `enum`, а не
     * булево, чтобы следующий потребитель не «переиспользовал» флаг.
     */
    scope: z.enum(['workload'], ruEnum('Недопустимое назначение списка')).optional(),
    /** Deprecated forward-only cursor (Phase 1). Accepted transiently. */
    cursor: UserListCursorSchema.optional(),
    /** Offset-pagination page index — 1-based. Default 1. */
    page: z.coerce.number().int().min(1).max(10_000).optional().default(1),
    /** Page size, default 50, max 200 (FE caps to 100). */
    limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  })
  .strict();
export type ListUsersQuery = z.infer<typeof ListUsersQuerySchema>;

/** Backwards-compatible alias for the prior name (kept until callers migrate). */
export const UserListQuerySchema = ListUsersQuerySchema;
export type UserListQuery = ListUsersQuery;

/** A single user row in list / get responses. Phase 2 adds `fullName`
 *  (migration 007 — User.fullName NOT NULL, backfilled from email
 *  local-part for legacy rows). */
export const UserSummarySchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  /**
   * R6 Wave 1 (W1-T1): `User.phone` is now nullable in the DB — the
   * invitee supplies the phone at activation, not the inviter at
   * invite-create. Pending users therefore carry `phone: null` until
   * they accept. The PATCH /users/:id body still requires E.164
   * (`PhoneSchema`) when supplied, but the read shape must accept
   * `null` so the FE table can render `—` for pending rows.
   */
  phone: z.string().nullable(),
  fullName: z.string().min(1).max(200),
  status: z.nativeEnum(UserStatus),
  departmentId: z.number().int().nullable(),
  roleId: z.number().int(),
  roleSlug: z.nativeEnum(RoleSlug),
  accessLevel: z.nativeEnum(AccessLevel),
  mustChangePassword: z.boolean(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * R6.4 — date of joining (ISO `YYYY-MM-DD`). Always present on read.
   * The BE backfilled existing rows from `createdAt` (migration 025).
   * Workload writes (plan + fact) reject `date < hireDate` with a 422
   * `BEFORE_HIRE_DATE`. The FE workload calendar disables ineligible
   * cells with a tooltip surfacing the joining date.
   */
  hireDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  version: z.number().int().nonnegative(),
  /**
   * Feature 1 «Помощник ГИПа» (Wave 1). For an ASSISTANT_GIP user, the id of
   * the GIP they assist; `null` for everyone else. Declared `.optional()` in
   * Wave 1 so the contract is fixed WITHOUT forcing the service layer to emit
   * it yet (no behaviour change this wave); a later wave makes it always-present
   * once `toSummary` is wired to surface the column.
   */
  assistantOfUserId: z.string().uuid().nullable().optional(),
});
export type UserSummary = z.infer<typeof UserSummarySchema>;

/**
 * Phase-2 list response shape (offset pagination per research-A §3).
 * The legacy `nextCursor` field is preserved transiently so existing
 * Phase-1 consumers do not break while W3 ships.
 */
export const UserListResponseSchema = z.object({
  rows: z.array(UserSummarySchema),
  page: z.number().int().nonnegative().optional(),
  limit: z.number().int().positive().optional(),
  totalRows: z.number().int().nonnegative().optional(),
  totalPages: z.number().int().nonnegative().optional(),
  /** Deprecated — retained until FE migrates fully (W3). */
  nextCursor: z.string().nullable().optional(),
});
export type UserListResponse = z.infer<typeof UserListResponseSchema>;

/**
 * Блок 2 · Ф8 · В-З1 — человек ЧУЖОГО отдела в списке выбора «Загруженности».
 *
 * Руководитель отдела, которому добавлено право «вся загруженность бюро»,
 * получает по `GET /users?scope=workload` людей всех отделов. Право — про
 * часы, загрузку и отсутствия (Р-185); контактов в нём нет. Поэтому человек
 * чужого отдела приходит узкой строкой — ровно то, что читает список выбора:
 * идентификатор, ФИО, отдел. Ни почты, ни телефона, ни даты приёма, ни версии.
 *
 * Люди СВОЕГО отдела в том же ответе приходят полной строкой
 * ({@link UserSummarySchema}) — как и без признака.
 */
export const UserPickerRowSchema = z
  .object({
    id: z.string().uuid(),
    fullName: z.string().min(1).max(200),
    departmentId: z.number().int().nullable(),
  })
  .strict();
export type UserPickerRow = z.infer<typeof UserPickerRowSchema>;

/**
 * Ответ `GET /users?scope=workload`: строки полные либо узкие. Без признака
 * сервер отдаёт прежний {@link UserListResponseSchema} — он не менялся.
 */
export const UserWorkloadListResponseSchema = UserListResponseSchema.extend({
  rows: z.array(z.union([UserSummarySchema, UserPickerRowSchema])),
});
export type UserWorkloadListResponse = z.infer<typeof UserWorkloadListResponseSchema>;

export type UserDetail = UserSummary;

// ─── PATCH /users/:id ──────────────────────────────────────────────────────

/**
 * PATCH body — fields are all optional but at least one must be set
 * (enforced at the service layer because zod's `.refine` would shadow
 * the field-level errors). `version` is REQUIRED — optimistic-lock per
 * T10.4.
 *
 * Field-level allowlist (T11.1):
 *   - email, phone, fullName — RO can set on own-dept employees;
 *     self can set own
 *   - roleId, departmentId, status — Admin only
 *
 * The service layer enforces the actor-vs-field allowlist; zod here is
 * the syntax / type contract.
 *
 * Phase 6 W0 T0.5 (DEFECT-02 / Q21): `phone` stays a non-null E.164
 * string at the schema level because the DB column is NOT NULL (it
 * doubles as a notification primary key per migration 007). The
 * controller pre-checks for an explicit `null` and returns 422
 * `PHONE_NOT_CLEARABLE` with an actionable message instead of the
 * generic VALIDATION_ERROR that zod would otherwise emit.
 */
/**
 * R6.4 — ISO date `YYYY-MM-DD`. Used by `hireDate` on the user surface.
 * Validated as a strict literal (4-2-2 digit groups) so we never accept
 * the `YYYY-MM-DDTHH:MM:SS` shape (which the Prisma JSON serialiser
 * would emit for plain `DateTime` columns) — that drift would land a
 * timestamp string into a `DATE` column and break the workload
 * comparison logic that assumes `date < hireDate` is a string compare.
 */
export const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Дата должна быть в формате ГГГГ-ММ-ДД' });

export const UpdateUserRequestSchema = z
  .object({
    version: z.number().int().nonnegative(),
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    fullName: FullNameSchema.optional(),
    roleId: z.number().int().positive().optional(),
    departmentId: z.number().int().positive().nullable().optional(),
    status: z.nativeEnum(UserStatus, ruEnum('Выберите статус')).optional(),
    /**
     * R6.4 — admin-editable joining date (ISO `YYYY-MM-DD`).
     * Field-level RBAC: ADMIN-tier can edit any user's hireDate; РО
     * (MANAGER) can edit own-dept Сотрудник hireDate; EMPLOYEE cannot.
     * The service layer enforces the matrix; zod here is the syntax.
     */
    hireDate: IsoDateSchema.optional(),
    /**
     * Feature 1 «Помощник ГИПа» (Wave 1). The GIP this user assists (only
     * meaningful for an ASSISTANT_GIP). `null` clears the binding. Added to the
     * `.strict()` body so a later wave's edit flow is not silently stripped;
     * field-level RBAC + the acts-as-GIP wiring land in later waves. Migration
     * 034 triggers enforce that the target is a GIP and the carrier is an
     * ASSISTANT_GIP.
     */
    assistantOfUserId: z.string().uuid().nullable().optional(),
    /** Required when self-edit changes email/phone (per Q22 / FIX-CURRENT-PWD).
     *  Server validates against the actor's password hash. */
    currentPassword: z.string().min(1).max(256).optional(),
  })
  .strict();
export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>;

// ─── POST /users/me/change-password ─────────────────────────────────────────

/**
 * Body for self-service password change (PLAN task 4.13c / FIX-MUST-CHANGE-PWD).
 *
 *   - `currentPassword` — required; verified by argon2 against the stored hash.
 *   - `newPassword` — full T4.3 policy (length + upper + digit + special).
 *
 * The server additionally:
 *   - rejects when `newPassword === currentPassword` (422 PASSWORD_REUSED).
 *   - clears the `mustChangePassword` flag on success.
 *   - revokes ALL active refresh tokens for the user (force re-login on
 *     other devices).
 *
 * `.strict()` rejects unknown keys with VALIDATION_ERROR — defense against
 * the "user smuggles roleId via change-password body" abuse case.
 */
export const ChangePasswordRequestSchema = z
  .object({
    currentPassword: z.string().min(1).max(256),
    newPassword: PasswordSchema,
  })
  .strict();
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;
